import { useContext, useState } from 'react';
import { Customer, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { AddNewItemAction, UpdateFilterAction } from '../../../lib/context/SmartSendContextModels';
import { getNamedFilter } from '../../../lib/helpers/DefaultFilters';
import { uid } from '../../../lib/helpers/uid';
import { ItemCreatePanel } from '../panels/ItemCreatePanel';
import { GenericEditProps } from './Edit';
import RenderCreateItemMessage from './RenderCreateItemMessage';
import { EventSections } from '../../../lib/events/EventLogger';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { Tagged } from '../../Tagged';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import useCreateItemsHelper from '~/apps/smartsend/lib/helpers/useItemCreateHelper';
import { P2GSidePanel } from '~/components/sidepanel';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SmartSendSubType } from '~/apps/smartsend/lib/consts/SmartSendSubType';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useOnLoad from '~/routes/use-on-load';
import useCountries from '~/hooks/use-countries';

type props = GenericEditProps & { sendTo?: Customer };

export default function Create(props: props) {
  const { createItemsHelper } = useCreateItemsHelper();
  const ctx = useContext(SmartSendContext);
  const [createMessages, setCreateMessages] = useState<string[]>([]);
  const { getCustomers, getPackages, getProducts } = useSmartSendApi();
  const { getLocalisedCountries } = useCountries();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const { track } = useAnalytics();

  useOnLoad(() => {
    track({
      type: 'create_item_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.CreateItem])
      }
    });
  });

  const saveNewItem = async (item: SmartSendItemApiModel) => {
    createItemsHelper(
      [{ id: uid(), item: item, raw: [] }],
      (items) => {
        const item = items[0];
        if (item) {
          ctx.dispatch(new AddNewItemAction(item, !item.Service?.Id));
        }
        ctx.dispatch(
          new UpdateFilterAction(SmartSendSubType, {
            ...ctx.state.filters[SmartSendSubType].filter,
            CurrentFilter: getNamedFilter('Post_Create')
          })
        );
      },
      () => {},
      (messages) => {
        if (messages.length > 0) {
          setCreateMessages(messages);
        } else {
          props.onClose();
        }
      }
    );
  };

  return (
    <P2GSidePanel
      open={true}
      onClose={props.onClose}
      anchor="right"
      title={t('NEW_ITEM_SIDEPANEL_TITLE', { defaultValue: 'New Item' })}
      width="590px"
    >
      <Tagged tag="panel-add-item">
        {createMessages.length > 0 ? (
          <RenderCreateItemMessage messages={createMessages} onClose={props.onClose} />
        ) : (
          <ItemCreatePanel
            hideDimensionsIfNotRequired={SettingsCache.settings.Features.includes('HideDimensionsIfNotRequired')}
            sender={ctx.state.customer}
            recipient={props.sendTo}
            getProducts={() => getProducts().then((r) => (r.success ? r.result.Products : []))}
            getPackages={() => getPackages().then((r) => (r.success ? r.result.Packages : []))}
            getCustomers={() => getCustomers().then((r) => (r.success ? r.result.Customers : []))}
            getCountries={() => getLocalisedCountries()}
            onSave={saveNewItem}
            close={props.onClose}
          />
        )}
      </Tagged>
    </P2GSidePanel>
  );
}
